<template>
  <div class="mtr">
    <!-- Texto: original o traducido. Si el chat formatea (links/saltos), reusamos su
         mismo formateador para no perder el estilo. -->
    <div v-if="formatter" class="mtr__text" v-html="rendered"></div>
    <div v-else class="mtr__text">{{ displayText }}</div>

    <div v-if="hasText" class="mtr__actions">
      <button
        class="mtr__btn"
        type="button"
        :disabled="translating"
        @click.stop.prevent="onTranslate"
      >
        <span v-if="translating" class="mtr__spin" aria-hidden="true"></span>
        <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 8 6 6"/><path d="m4 14 6-6 2-3"/><path d="M2 5h12"/><path d="M7 2h1"/><path d="m22 22-5-10-5 10"/><path d="M14 18h6"/></svg>
        <span>{{ label }}</span>
      </button>

      <span v-if="laTraduccion && !showOriginal" class="mtr__badge">
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        {{ $t("mensajesChat.translate.translated") }}
      </span>

      <button
        class="mtr__btn mtr__btn--icon"
        type="button"
        :title="copied ? $t('mensajesChat.translate.copied') : $t('mensajesChat.translate.copy')"
        @click.stop.prevent="onCopy"
      >
        <svg v-if="!copied" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      </button>
    </div>
  </div>
</template>

<script>
import axios from "axios";

/**
 * Traducción por-mensaje con IA para las burbujas del chat (WhatsApp, mensajes web y
 * OTA). Reutilizable: recibe el texto crudo + property_id + (opcional) el formateador
 * del propio chat para conservar links/saltos. Estado por instancia → cada burbuja
 * traduce y alterna "Ver original / Ver traducción" de forma independiente.
 * Endpoint: POST /translate/text { text, property_id } → { ok, translated }.
 */
export default {
  name: "MessageTranslate",
  props: {
    text: { type: String, default: "" },
    propertyId: { type: [Number, String], default: null },
    formatter: { type: Function, default: null },
    /**
     * La traducción YA hecha, cuando el traductor del chat está encendido.
     *
     * Con el automático, las traducciones se piden todas juntas desde la
     * pantalla (un viaje para toda la conversación) y bajan aquí hechas: si
     * cada burbuja pidiera la suya, abrir un chat de treinta mensajes serían
     * treinta llamadas al modelo. El botón sigue sirviendo para ver el
     * original.
     */
    translation: { type: String, default: null },
  },
  data() {
    return {
      translated: null,
      translating: false,
      showOriginal: false,
      copied: false,
    };
  },
  computed: {
    hasText() {
      return !!(this.text && String(this.text).trim());
    },
    /** La que haya: la que bajó hecha o la que pidió esta burbuja. */
    laTraduccion() {
      return this.translation || this.translated || null;
    },
    displayText() {
      return this.laTraduccion && !this.showOriginal ? this.laTraduccion : this.text;
    },
    rendered() {
      return this.formatter ? this.formatter(this.displayText) : this.displayText;
    },
    label() {
      if (!this.laTraduccion) return this.$t("mensajesChat.translate.translate");
      return this.showOriginal
        ? this.$t("mensajesChat.translate.viewTranslation")
        : this.$t("mensajesChat.translate.viewOriginal");
    },
  },
  watch: {
    // Si el texto de la burbuja cambia (edición/re-render), reinicia la traducción.
    text() {
      this.translated = null;
      this.showOriginal = false;
    },
  },
  methods: {
    async onTranslate() {
      // Ya traducido (aquí o desde la pantalla) → alterna original / traducción.
      if (this.laTraduccion) {
        this.showOriginal = !this.showOriginal;
        return;
      }
      if (this.translating || !this.hasText) return;
      this.translating = true;
      try {
        const { data } = await axios.post("/translate/text", {
          text: this.text,
          property_id: this.propertyId != null ? this.propertyId : undefined,
        });
        if (data && data.ok && data.translated) {
          this.translated = String(data.translated);
          this.showOriginal = false;
        } else {
          this.notifyError(data && data.error);
        }
      } catch (e) {
        this.notifyError(
          (e && e.response && e.response.data && (e.response.data.error || e.response.data.message)) ||
            (e && e.message),
        );
      } finally {
        this.translating = false;
      }
    },
    notifyError(detail) {
      const msg = detail || this.$t("mensajesChat.translate.error");
      if (this.$toast && this.$toast.error) this.$toast.error(msg);
      else if (window.$toast && window.$toast.error) window.$toast.error(msg);
    },
    async onCopy() {
      const value = String(this.displayText || "");
      try {
        await navigator.clipboard.writeText(value);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = value;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch (e) {}
        document.body.removeChild(ta);
      }
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 1500);
    },
  },
};
</script>

<style scoped>
/* El texto hereda la tipografía/color de la burbuja del chat (no forzamos nada). */
.mtr__text {
  white-space: pre-wrap;
  word-break: break-word;
}
.mtr__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 6px;
}
.mtr__btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1;
  color: currentColor;
  opacity: 0.62;
  transition: opacity 0.12s ease;
}
.mtr__btn:hover { opacity: 0.95; }
.mtr__btn:disabled { opacity: 0.4; cursor: default; }
.mtr__btn--icon { gap: 0; }
.mtr__badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #15803d;
  background: rgba(22, 163, 74, 0.12);
  padding: 1px 7px 1px 6px;
  border-radius: 999px;
}
.mtr__spin {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-top-color: transparent;
  display: inline-block;
  animation: mtr-spin 0.6s linear infinite;
}
@keyframes mtr-spin {
  to { transform: rotate(360deg); }
}
</style>
